import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, NgControl, Validators } from '@angular/forms';
import { customRequiredValidation } from '@irian-ro/shared-components-module';
import { SelectItem } from 'primeng/api';
import { ConfirmationDialogModel } from '@well/confirmation-dialog';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { SignupLegalEntityInputsService } from './signup-legal-entity-inputs.service';
import { CompanyType, LegalEntityModel } from '@well/common-libs/legal-entity-management/domain-model';
import { CountryVoModel } from '@well/common-libs/country-management/shell-api';
import { CompanyDetailsDtoModel } from '@well/domain';
import { FormUtils } from '@irian-ro/util-forms';
import { translationSchema } from '@well/translation';
import { constants } from '@well/infra/constants';
import {
  AbstractLegalEntitySearchService,
  CompanyLegalEntitySignupSearchService,
} from '@well/common-libs/legal-entity-management/data-access';

@Component({
  selector: 'signup-legal-entity-inputs',
  templateUrl: './signup-legal-entity-inputs.component.html',
  styleUrls: ['./signup-legal-entity-inputs.component.scss'],
  providers: [
    SignupLegalEntityInputsService,
    {
      provide: AbstractLegalEntitySearchService,
      useClass: CompanyLegalEntitySignupSearchService,
    }
  ],
})
export class SignupLegalEntityInputsComponent implements OnInit {
  _type: CompanyType | undefined = undefined;

  hasInvoiceSeriesPrefix = false;

  displayLegalEntityFields = false;

  selectedLegalEntity: LegalEntityModel | null = null;

  addManualCompany = false;

  private confirmationDialogEnabled = true;
  private confirmationState = false;
  private dialogData: ConfirmationDialogModel | undefined = undefined;
  private dialogTranslations: any | undefined = undefined;

  uniqueIdNumberPattern = constants.regexConstants.uniqueIdNumberPattern;

  @Input()
  set type(value: CompanyType) {
    this._type = value;
    this.updateHasInvoiceSeriesPrefix();
  }

  private _legalEntity: LegalEntityModel | undefined = undefined;
  @Input() set legalEntity(value: LegalEntityModel) {
    if (value) {
      this._legalEntity = value;

      if (!this._legalEntity.industryTypeValue) {
        this.industryTypeValueFormControl.enable();
      }

      this.updateFormValues(value);
      if (this._legalEntity.uuid) {
        this.showLegalEntityFields();
      }
    }
  }

  @Input()
  legalEntitySearchLocation: 'ANAF' | 'ALL' = 'ALL';

  get type(): CompanyType | undefined {
    return this._type;
  }

  get legalEntity(): LegalEntityModel | undefined {
    return this._legalEntity;
  }

  form: FormGroup | undefined = undefined;

  countryResourceType = CountryVoModel;

  languages: SelectItem[] = [
    { label: 'Română', value: 'ro' },
    { label: 'English', value: 'en' }
  ];

  @Output() save: EventEmitter<CompanyDetailsDtoModel> = new EventEmitter<CompanyDetailsDtoModel>();

  constructor(
    private translateService: TranslateService,
    private signupLegalEntityInputsService: SignupLegalEntityInputsService
  ) {
    this.form = this.createForm();
    //this.updateHasInvoiceSeriesPrefix();
    this.vatInactiveStatusFormControl.disable();
    this.vatRegistrationFormControl.disable();
    this.vatSplitStatusFormControl.disable();
    this.vatInactivationDateFormControl.disable();
  }

  ngOnInit(): void {
    this.getDialogTranslations();
    if (this._legalEntity?.uuid) {
      this.form?.disable();
    }
  }

  private getDialogTranslations() {
    this.translateService.stream('legalEntity.confirmationDialog').subscribe((res) => {
      this.dialogTranslations = res;
    });
  }

  get officeAddressFormGroup() {
    return this.form?.get('legalEntityDTO.officeAddressDTO') as FormGroup;
  }

  get financialAddressFormGroup() {
    return this.form?.get('legalEntityDTO.financialAddressDTO') as FormGroup;
  }

  get vatRegistrationFormControl() {
    return this.form?.get('legalEntityDTO.vatRegistration') as FormControl;
  }

  get vatSplitStatusFormControl() {
    return this.form?.get('legalEntityDTO.vatSplitStatus') as FormControl;
  }

  get vatInactiveStatusFormControl() {
    return this.form?.get('legalEntityDTO.vatInactiveStatus') as FormControl;
  }

  get invoiceSeriesPrefixFormControl() {
    return this.form?.get('invoiceSeriesPrefix') as FormControl;
  }

  get uniqueIdNumberFormControl() {
    return this.form?.get('legalEntityDTO.uniqueIdNumber') as FormControl;
  }

  get industryTypeValueFormControl() {
    return this.form?.get('legalEntityDTO.industryTypeValue') as FormControl;
  }

  get vatInactivationDateFormControl() {
    return this.form?.get('legalEntityDTO.vatInactivationDate') as FormControl;
  }

  get legalEntityFormGroup() {
    return this.form?.get('legalEntityDTO') as FormGroup;
  }

  createForm() {
    return new FormGroup({
      legalEntityDTO: new FormGroup({
        companyRegistrationUuid: new FormControl(null),
        legalEntityUuid: new FormControl(null),
        companyName: new FormControl(null, customRequiredValidation('CompanyName')),
        uniqueIdNumber: new FormControl(null, customRequiredValidation('UniqueIdNumber')),
        registrationCode: new FormControl(null),
        country: new FormControl(null, customRequiredValidation('Country')),
        officeAddressDTO: new FormControl(null, Validators.required),
        financialAddressDTO: new FormControl(null, Validators.required),
        mainPhone: new FormControl(null),
        mainEmailAddress: new FormControl(null),
        industryTypeValue: new FormControl(null),
        preferredLanguage: new FormControl(null, customRequiredValidation('PreferredLanguage')),
        vatRegistration: new FormControl(null),
        uuid: new FormControl(null),
        vatSplitStatus: new FormControl(null),
        vatInactiveStatus: new FormControl(null),
        vatInactivationDate: new FormControl(null),
        validated: new FormControl(null),
      }),
      invoiceSeriesPrefix: new FormControl(null),
    });
  }

  onSubmit() {
    if(this.form) {
      FormUtils.markAllAsTouched(this.form);
      FormUtils.markAllAsDirty(this.form);
    }

    this.form?.updateValueAndValidity({ emitEvent: true });
    if (this.form?.disabled || this.form?.valid) {
      this.save.emit(this.form.getRawValue());
    }
  }

  updateHasInvoiceSeriesPrefix() {
    this.hasInvoiceSeriesPrefix =
      this.type === CompanyType.forwarder ||
      this.type === CompanyType.service_provider ||
      this.type === CompanyType.customer_with_fleet;
    if (this.form) {
      if (this.hasInvoiceSeriesPrefix) {
        this.invoiceSeriesPrefixFormControl.setValidators(customRequiredValidation('InvoiceSeriesPrefix'));
      } else {
        this.invoiceSeriesPrefixFormControl.setValidators(null);
      }
    }
  }

  onLegalEntitySelect(legalEntity: LegalEntityModel) {
    this.selectedLegalEntity = legalEntity;
    this.showLegalEntityFields();
    this.form?.enable();
    if(this.form) {
      FormUtils.markAllAsTouched(this.form);
      FormUtils.markAllAsDirty(this.form);
    }
    if (legalEntity.validated) {
      this.form?.disable();
      this.updateFormValues(legalEntity);
      if (!legalEntity.officeAddressDTO || this.checkAddressRequiredFields(legalEntity.officeAddressDTO)) {
        this.officeAddressFormGroup.enable();
        this.form?.updateValueAndValidity();
      }
      if (!legalEntity.financialAddressDTO || this.checkAddressRequiredFields(legalEntity.financialAddressDTO)) {
        this.financialAddressFormGroup.enable();
        this.form?.updateValueAndValidity();
      }
    } else {
      if (legalEntity.origin === 'LOCAL') {
        this.onLocalLegalEntitySelected(legalEntity);
        this.enableAnafAutocompletedFields();
      } else {
        this.disableAnafAutocompletedFields();
        this.resetAllFields();
        this.updateFormValues(legalEntity);
      }
    }
  }

  private onLocalLegalEntitySelected(legalEntity: LegalEntityModel) {
    if(!legalEntity.uuid) {
      return;
    }

    this.signupLegalEntityInputsService
      .getCompanyDetailsByUuid(legalEntity.uuid)
      .pipe(map((res) => res.data))
      .subscribe((companyDetails: any) => {
        this.legalEntityFormGroup.disable();

        this.resetAllFields();
        if (this.type === CompanyType.service_provider || this.type === CompanyType.forwarder) {
          this.invoiceSeriesPrefixFormControl.patchValue(companyDetails.invoiceSeriesPrefix);
        }
        this.updateFormValues(companyDetails.legalEntityDTO);
        if (!legalEntity.officeAddressDTO) {
          this.officeAddressFormGroup.enable();
          this.form?.updateValueAndValidity();
        }
        if (!legalEntity.financialAddressDTO) {
          this.financialAddressFormGroup.enable();
          this.form?.updateValueAndValidity();
        }
      });
  }

  checkAddressRequiredFields(obj: any): boolean {
    return ['country', 'postalCode', 'city'].some((key) => !obj[key]);
  }

  private showLegalEntityFields() {
    this.displayLegalEntityFields = true;
  }

  private disableAnafAutocompletedFields() {
    this.uniqueIdNumberFormControl.disable();
  }

  private enableAnafAutocompletedFields() {
    this.uniqueIdNumberFormControl.enable();
  }

  private resetAllFields() {
    this.form?.reset({});
  }

  private updateFormValues(legalEntityModel: LegalEntityModel) {
    this.legalEntityFormGroup.patchValue(legalEntityModel);
    this.form?.updateValueAndValidity();
  }

  addNewCompany() {
    this.confirmationDialogEnabled = false;
    this.showLegalEntityFields();
    this.form?.enable();
    this.addManualCompany = true;
    this.selectedLegalEntity = null;
  }

  private acceptDialog = (control: NgControl) => {
    this.confirmationState = true;
  };

  private cancelDialog = (control: NgControl) => {
    control.valueAccessor!.writeValue(!control.value);
  };

  getDumpDialogData() {
    this.dialogData = {
      enabled: this.confirmationDialogEnabled,
      dialogHeader: this.dialogTranslations.dialogHeader,
      dialogMessage: this.dialogTranslations.dialogMessage,
      acceptLabel: this.dialogTranslations.acceptLabel,
      cancelLabel: this.dialogTranslations.cancelLabel,
      confirmationState: this.confirmationState,
      acceptCallback: this.acceptDialog,
      cancelCallback: this.cancelDialog,
    };

    return this.dialogData;
  }

  hideLegalEntityFields() {
    this.resetDialogState();
    this.addManualCompany = false;
    this.displayLegalEntityFields = false;
    this.enableAnafAutocompletedFields();
    this.resetAllFields();
  }

  private resetDialogState() {
    this.confirmationDialogEnabled = true;
    this.confirmationState = false;
  }

  protected readonly translationSchema = translationSchema;
}
